import React from 'react';
import PropTypes from 'prop-types';
import Table from '../../../components/table/Table';
import ProfilePicture from '../../../components/profilePicture/ProfilePicture';
import Tag from '../../../components/tag/Tag';
import styles from './ProgramStudentsTable.module.scss';
import { getSpecialityTagVariant } from '../../../utils/specialityUtils';

const ProgramStudentsTable = ({ students = [], isSelectable = false, onRowSelect, selectedRows, onAssignClinicalDays, onAssignNonClinicalDays, onAutoSchedule }) => {

    const columns = [
        {
            header: 'Student',
            key: 'student',
            render: (row) => (
                <div className={styles.studentCell}>
                    <ProfilePicture
                        profilePicture={row.profilePicture}
                        firstName={row.firstName}
                        lastName={row.lastName}
                        size='xsm'
                    />
                    <span>{`${row.firstName} ${row.lastName}`}</span>
                </div>
            )
        },
        {
            header: 'Instructor',
            key: 'instructor',
            render: (row) => row.instructors?.length > 0 ? (
                <div className={styles.instructorList}>
                    {row.instructors.map((instructor) => (
                        <div key={instructor.id} className={styles.instructorItem}>
                            <ProfilePicture
                                profilePicture={instructor.profilePicture}
                                firstName={instructor.firstName}
                                lastName={instructor.lastName}
                                size='xs'
                            />
                            <span>{`${instructor.firstName} ${instructor.lastName}`}</span>
                        </div>
                    ))}
                </div>
            ) : 'Unassigned'
        },
        {
            header: 'Specialty',
            key: 'specialty',
            render: (row) => (
                <div className={styles.specialtyList}>
                    {row.specialities?.map((spec) => (
                        <Tag
                            key={spec.id}
                            text={spec.name}
                            variant={getSpecialityTagVariant(spec.name)}
                            rounded={true}
                        />
                    ))}
                </div>
            )
        },
        {
            header: 'Clinical Site',
            key: 'clinicalSite',
            render: (row) => row.clinicalSites?.length > 0 ? (
                <div className={styles.clinicalSiteList}>
                    {row.clinicalSites.map((site) => (
                        <span key={site.id} className={styles.clinicalSiteItem}>{site.name}</span>
                    ))}
                </div>
            ) : 'Unassigned'
        },
        {
            header: 'Year',
            key: 'year',
            render: (row) => row.year?.year
        },
        {
            header: 'Auto Schedule',
            key: 'autoSchedule',
            render: (row) => (
                <span className={`${styles.availabilityTag} ${!row.excludeFromAutoSchedule ? styles.available : styles.unavailable}`}>
                    {!row.excludeFromAutoSchedule ? 'Enabled' : 'Disabled'}
                </span>
            )
        },
    ];

    const getActionItems = (onAssignClinicalDays || onAssignNonClinicalDays)
        ? (row) => [
            { label: 'Assign Clinical Days', onClick: () => onAssignClinicalDays?.(row.id) },
            { label: 'Assign Non Clinical Days', onClick: () => onAssignNonClinicalDays?.(row.id) },
            row.excludeFromAutoSchedule
                ? { label: 'Enable Auto Schedule', onClick: () => onAutoSchedule?.([row.id], false) }
                : { label: 'Disable Auto Schedule', onClick: () => onAutoSchedule?.([row.id], true) },
        ]
        : undefined;

    return (
        <Table
            columns={columns}
            data={students}
            onRowSelect={isSelectable ? onRowSelect : undefined}
            selectedRows={isSelectable ? selectedRows : undefined}
            getActionItems={getActionItems}
        />
    );
};

ProgramStudentsTable.propTypes = {
  students: PropTypes.array,
  isSelectable: PropTypes.bool,
  onRowSelect: PropTypes.func,
  selectedRows: PropTypes.array,
  onAssignClinicalDays: PropTypes.func,
  onAssignNonClinicalDays: PropTypes.func,
  onAutoSchedule: PropTypes.func
};

export default ProgramStudentsTable;
